import { useMemo, useState } from "react";
import {
  Search, Calculator, Heart, Brain, Activity,
  Stethoscope, Syringe, Users, Lock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useClinicalTools, useToggleFavourite, type ClinicalTool } from "@/lib/clinical-tools";
import { ListingSkeletons } from "@/components/ListingCard";
import { ButtonLink, EmptyState, Status, buttonClass } from "@/components/system";

/**
 * Clinician Resources — tool library.
 *
 * Restored to match the original referral-flow-canberra concept, merged
 * with PulseBoard's additions:
 *   - Search bar at top (fuzzy over name + description + tags)
 *   - Category filter chips (All + every populated category)
 *   - Distinct icon per tool (visual identity at a glance)
 *   - Clear CTA state on every card: live tools launch, future tools are marked coming soon.
 *   - Flat grid
 *
 * Authenticated users additionally get:
 *   - Favourite pinning (hearts, optimistic update)
 *   - Usage analytics via /v1/tools/:slug/usage
 *
 * Unauthenticated visitors see the full catalogue (public endpoint),
 * with heart disabled and the launch button rendered as "Sign in to
 * use". This keeps the library discoverable (SEO + trust) without
 * leaking authed-only data (favourites, usage).
 *
 * `last_reviewed_at` is intentionally NOT shown on cards (clutter); it
 * lives on the detail page at /tools/:slug.
 */

// Icon per tool — mirrors the concept's visual identity. Maps by slug so
// new tools can slot in without a schema change. Unknown slugs fall
// through to the generic Calculator.
const TOOL_ICON: Record<string, React.ElementType> = {
  "colorectal-cancer-screening": Activity,
  "breast-cancer-screening": Heart,
  "cervical-hpv": Users,
  "lung-cancer-screening": Stethoscope,
  "t2d-risk": Calculator,
  "frax": Activity,
  "smoking-vaping": Brain,
  "kidney-screener": Stethoscope,
  "falls-risk": Users,
  "sti-router": Activity,
  "gad2": Brain,
  "immunisation-gap": Syringe,
};

function iconFor(slug: string): React.ElementType {
  return TOOL_ICON[slug] ?? Calculator;
}

interface ToolCardProps {
  tool: ClinicalTool;
  isAuthenticated: boolean;
  onFavourite: (slug: string, next: boolean) => void;
}

function ToolCard({ tool, isAuthenticated, onFavourite }: ToolCardProps) {
  const Icon = iconFor(tool.slug);
  const isLive = tool.implementation_key === "gad2-v1";
  const href = `/tools/${tool.slug}`;
  const isFavourite = Boolean(tool.is_favourite);

  const favouriteLabel = isAuthenticated ? (isFavourite ? "Remove from favourites" : "Pin to favourites") : "Sign in to pin";

  return (
    <article className="ds-card ds-tool">
      <div className="ds-listing__top">
        <span className="ds-tool__icon" aria-hidden="true"><Icon /></span>
        <span className="ds-cluster" style={{ gap: 8 }}>
          <span className="ds-eyebrow">{tool.category_name}</span>
          <button
            type="button"
            className={buttonClass({ variant: "ghost", size: "sm", iconOnly: true })}
            onClick={() => {
              if (!isAuthenticated) return;
              onFavourite(tool.slug, !isFavourite);
            }}
            disabled={!isAuthenticated}
            title={favouriteLabel}
            aria-label={favouriteLabel}
            aria-pressed={isAuthenticated ? isFavourite : undefined}
          >
            <Heart aria-hidden="true" className={isFavourite ? "ds-tool__fav" : undefined} />
          </button>
        </span>
      </div>
      <div className="ds-stack" style={{ gap: 6 }}>
        <h3 className="ds-listing__name">{tool.name}</h3>
        <div><Status tone={isLive ? "ok" : "neutral"}>{isLive ? "Live" : "Coming soon"}</Status></div>
      </div>
      <p className="ds-small">{tool.description}</p>
      <div className="ds-stack" style={{ gap: 10, alignSelf: "end" }}>
        {tool.reference ? <p className="ds-fine">Reference: {tool.reference}</p> : null}
        {isLive && isAuthenticated ? (
          <ButtonLink to={href} block>Open tool</ButtonLink>
        ) : isLive ? (
          <ButtonLink to="/auth" variant="secondary" block>
            <Lock aria-hidden="true" />
            Sign in to use
          </ButtonLink>
        ) : (
          <ButtonLink to={href} variant="secondary" block>View preview</ButtonLink>
        )}
      </div>
    </article>
  );
}

export function ClinicalToolsSection() {
  const { isAuthenticated } = useAuth();
  const { data, isLoading, error } = useClinicalTools();
  const { mutate: toggleFav } = useToggleFavourite();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  // Derive category chips from the catalogue, preserving sort_order.
  // Empty categories never render a dead chip.
  const categories = useMemo<string[]>(() => {
    if (!data) return ["All"];
    const seen = new Map<number, string>();
    for (const t of data.items) seen.set(t.category_sort_order, t.category_name);
    const ordered = [...seen.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, name]) => name);
    return ["All", ...ordered];
  }, [data]);

  const filteredTools = useMemo<ClinicalTool[]>(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();
    const matching = data.items.filter((t) => {
      const matchesCategory = selectedCategory === "All" || t.category_name === selectedCategory;
      const matchesSearch =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });

    // When "All" is selected and user is authed, float favourites to the
    // top. Within a category filter, stable alphabetical is cleaner.
    if (selectedCategory !== "All") return matching;
    return [...matching].sort((a, b) => {
      const fa = a.is_favourite ? 0 : 1;
      const fb = b.is_favourite ? 0 : 1;
      if (fa !== fb) return fa - fb;
      if (a.category_sort_order !== b.category_sort_order) {
        return a.category_sort_order - b.category_sort_order;
      }
      return a.name.localeCompare(b.name);
    });
  }, [data, searchQuery, selectedCategory]);

  if (isLoading) {
    return <ListingSkeletons />;
  }

  if (error || !data) {
    return (
      <EmptyState tone="error" icon={<Calculator />} title="The tool library didn't load">
        Try again shortly. RACGP clinical guidelines remain available at racgp.org.au.
      </EmptyState>
    );
  }

  return (
    <div className="ds-stack" style={{ gap: 16 }}>
      <div className="ds-panel ds-panel--tight ds-toolbar">
        <div className="ds-toolbar__search" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
          <label htmlFor="tools-q" className="ds-visually-hidden">Search tools</label>
          <Search aria-hidden="true" />
          <input
            id="tools-q"
            className="ds-input"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tools, conditions or guidelines"
          />
        </div>
        <div className="ds-toolbar__filters">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className="ds-chip"
              aria-pressed={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
          <span className="ds-fine ds-toolbar__count">Showing {filteredTools.length} of {data.items.length}</span>
        </div>
      </div>

      {filteredTools.length === 0 ? (
        <EmptyState icon={<Search />} title="No tools match that search">
          Try a broader term or another category.
        </EmptyState>
      ) : (
        <div className="ds-grid">
          {filteredTools.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              isAuthenticated={isAuthenticated}
              onFavourite={(slug, next) => toggleFav({ slug, favourite: next })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
